import { z } from 'zod'

import type { BillingRunPreview, BillingRunQueue } from '@scrambl/shared'
import {
  BillingScheduleCadenceSchema,
  BillingScheduleCategorySchema,
  BillingScheduleLineSchema,
  BillingScheduleLineSourceSchema,
  BillingScheduleLineStatusSchema,
  calendarDateFromStorage,
  isCalendarDate,
  isValidUUID,
  PaginationMetaSchema,
} from '@scrambl/shared'

const id = z.string().refine(isValidUUID)
const groupId = z.union([id, z.literal('client-level')])
const amount = z.string().regex(/^-?\d{1,38}\.\d{2}$/)
const hours = z.string().regex(/^\d{1,38}\.\d{2}$/)
const count = z.number().int().nonnegative().safe()
const currency = z.string().regex(/^[A-Z]{3}$/)
const day = z.string().refine(isCalendarDate)
const period = z
  .object({ start: day, end: day })
  .refine(value => value.start <= value.end)
  .nullable()
const timeExclusion = z.object({
  reason: z.enum([
    'ALREADY_INVOICED',
    'BILLED_EXTERNALLY',
    'RETAINER_COVERED',
    'NOT_APPROVED',
    'NON_BILLABLE',
  ]),
  entryCount: count,
  hours,
  amount,
})
const expenseExclusion = z.object({
  reason: z.enum([
    'ALREADY_INVOICED',
    'NOT_APPROVED',
    'NON_BILLABLE',
    'CURRENCY_MISMATCH',
  ]),
  expenseCount: count,
  amount,
  currencies: z.array(currency).optional(),
})
const timeLine = z.object({
  key: z.string(),
  memberId: id,
  memberName: z.string(),
  roleId: id.nullable(),
  roleName: z.string(),
  hours,
  billRate: amount,
  lineAmount: amount,
  entryIds: z.array(id).min(1),
})
const expenseLine = z.object({
  key: z.string(),
  expenseId: id,
  merchant: z.string(),
  description: z.string().nullable(),
  categoryName: z.string().nullable(),
  memberId: id,
  memberName: z.string(),
  expenseDate: day,
  amount,
})
const previewSchema = z.object({
  entityId: id,
  entityName: z.string(),
  currency,
  period,
  totalAmount: amount,
  timeAmount: amount,
  expenseAmount: amount,
  totalHours: hours,
  entryCount: count,
  expenseCount: count,
  projectCount: count,
  memberCount: count,
  projects: z.array(
    z.object({
      projectId: groupId,
      projectName: z.string(),
      projectCode: z.string().nullable(),
      hours,
      totalAmount: amount,
      timeAmount: amount,
      expenseAmount: amount,
      entryCount: count,
      expenseCount: count,
      lines: z.array(timeLine),
      expenseLines: z.array(expenseLine),
      retainerContracts: z.array(
        z.object({ contractId: id, contractTitle: z.string() }),
      ),
      exclusions: z.array(timeExclusion),
      expenseExclusions: z.array(expenseExclusion),
    }),
  ),
  exclusions: z.array(timeExclusion),
  expenseExclusions: z.array(expenseExclusion),
})
const queueSchema = z.object({
  period,
  totalAmount: amount.nullable(),
  currency: currency.nullable(),
  expenseCount: count,
  byCurrency: z.array(
    z.object({ currency, totalAmount: amount, expenseCount: count }),
  ),
  clients: z.array(
    z.object({
      entityId: id,
      entityName: z.string(),
      currency,
      expenseCount: count,
      totalAmount: amount,
      groups: z.array(
        z.object({
          groupId,
          groupName: z.string(),
          expenseCount: count,
          amount,
          lines: z.array(expenseLine),
        }),
      ),
      exclusions: z.array(expenseExclusion),
    }),
  ),
})
export class BillingReadContractError extends Error {
  constructor(
    message = 'Billing information is unavailable. Reload it before continuing.',
  ) {
    super(message)
    this.name = 'BillingReadContractError'
  }
}
const fail = () => {
  throw new BillingReadContractError()
}
export const billingCents = (value: string): bigint =>
  BigInt(value.replace('.', ''))
export function isBillingNumericAmount(
  value: unknown,
  integerDigits = 13,
): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    Math.abs(value) < 10 ** integerDigits &&
    Number(value.toFixed(2)) === value
  )
}
const sum = (values: string[]) =>
  values.reduce((total, value) => total + billingCents(value), 0n)
function unique(values: string[]) {
  if (new Set(values).size !== values.length) fail()
}
function assertPeriod(
  actual: z.infer<typeof period>,
  params: { periodStart?: string; periodEnd?: string },
  inferred: boolean,
) {
  if (params.periodStart && params.periodEnd) {
    if (
      !actual ||
      actual.start !== params.periodStart ||
      actual.end !== params.periodEnd
    )
      fail()
  } else if (!inferred && actual !== null) fail()
  else if (
    actual &&
    ((params.periodStart && actual.start < params.periodStart) ||
      (params.periodEnd && actual.end > params.periodEnd))
  )
    fail()
}
function assertExpenses(
  lines: z.infer<typeof expenseLine>[],
  parent: string,
  params: { periodStart?: string; periodEnd?: string },
  seen: Set<string>,
) {
  for (const line of lines) {
    if (
      line.key !== `${parent}:expense:${line.expenseId}` ||
      seen.has(line.expenseId) ||
      (params.periodStart && line.expenseDate < params.periodStart) ||
      (params.periodEnd && line.expenseDate > params.periodEnd)
    )
      fail()
    seen.add(line.expenseId)
  }
}
function assertExclusions(
  time: z.infer<typeof timeExclusion>[],
  expenses: z.infer<typeof expenseExclusion>[],
) {
  unique(time.map(row => row.reason))
  unique(expenses.map(row => row.reason))
  if (
    time.some(
      row => row.reason === 'NON_BILLABLE' && billingCents(row.amount) !== 0n,
    )
  )
    fail()
  for (const row of expenses) {
    unique(row.currencies ?? [])
    if (row.reason === 'CURRENCY_MISMATCH' && billingCents(row.amount) !== 0n)
      fail()
    if (row.reason !== 'CURRENCY_MISMATCH' && row.currencies?.length) fail()
  }
}
function assertExclusionCounts<T extends { reason?: string }>(
  summary: T[],
  groups: T[][],
  counter: (row: T) => number,
) {
  const counts = new Map<string, number>()
  for (const row of groups.flat())
    counts.set(row.reason, (counts.get(row.reason) ?? 0) + counter(row))
  if (
    summary.length !== counts.size ||
    summary.some(row => counter(row) !== counts.get(row.reason))
  )
    fail()
}
// Explicit projections keep DTO required keys intact under the web workspace's
// non-strict-null compiler setting, while the schemas enforce them at runtime.
const projectPeriod = (value: z.infer<typeof period>) =>
  value ? { start: value.start, end: value.end } : null
const projectTimeLine = (line: z.infer<typeof timeLine>) => ({
  key: line.key,
  memberId: line.memberId,
  memberName: line.memberName,
  roleId: line.roleId,
  roleName: line.roleName,
  hours: line.hours,
  billRate: line.billRate,
  lineAmount: line.lineAmount,
  entryIds: line.entryIds,
})
const projectExpenseLine = (line: z.infer<typeof expenseLine>) => ({
  key: line.key,
  expenseId: line.expenseId,
  merchant: line.merchant,
  description: line.description,
  categoryName: line.categoryName,
  memberId: line.memberId,
  memberName: line.memberName,
  expenseDate: line.expenseDate,
  amount: line.amount,
})
const projectTimeExclusion = (row: z.infer<typeof timeExclusion>) => ({
  reason: row.reason,
  entryCount: row.entryCount,
  hours: row.hours,
  amount: row.amount,
})
const projectExpenseExclusion = (row: z.infer<typeof expenseExclusion>) => ({
  reason: row.reason,
  expenseCount: row.expenseCount,
  amount: row.amount,
  ...(row.currencies ? { currencies: row.currencies } : {}),
})
export function billingPreview(
  value: unknown,
  entityId: string,
  params: { periodStart?: string; periodEnd?: string },
): BillingRunPreview {
  const parsed = previewSchema.safeParse(value)
  if (!parsed.success) return fail()
  const row = parsed.data
  if (row.entityId !== entityId) fail()
  assertPeriod(row.period, params, true)
  const entries = new Set<string>(),
    expenses = new Set<string>(),
    members = new Set<string>()
  unique(row.projects.map(group => group.projectId))
  for (const group of row.projects) {
    unique(group.lines.map(line => line.key))
    unique(group.retainerContracts.map(contract => contract.contractId))
    for (const line of group.lines) {
      if (
        line.key !==
        `${group.projectId}:${line.memberId}:${line.roleId ?? 'none'}`
      )
        fail()
      for (const id of line.entryIds) {
        if (entries.has(id)) fail()
        entries.add(id)
      }
      members.add(line.memberId)
    }
    assertExpenses(group.expenseLines, group.projectId, params, expenses)
    group.expenseLines.forEach(line => members.add(line.memberId))
    assertExclusions(group.exclusions, group.expenseExclusions)
    if (
      group.entryCount !==
        group.lines.reduce((total, line) => total + line.entryIds.length, 0) ||
      group.expenseCount !== group.expenseLines.length ||
      billingCents(group.timeAmount) !==
        sum(group.lines.map(line => line.lineAmount)) ||
      billingCents(group.expenseAmount) !==
        sum(group.expenseLines.map(line => line.amount)) ||
      billingCents(group.totalAmount) !==
        billingCents(group.timeAmount) + billingCents(group.expenseAmount)
    )
      fail()
  }
  assertExclusions(row.exclusions, row.expenseExclusions)
  assertExclusionCounts(
    row.exclusions,
    row.projects.map(group => group.exclusions),
    row => row.entryCount,
  )
  assertExclusionCounts(
    row.expenseExclusions,
    row.projects.map(group => group.expenseExclusions),
    row => row.expenseCount,
  )
  if (
    row.entryCount !== entries.size ||
    row.expenseCount !== expenses.size ||
    row.memberCount !== members.size ||
    row.projectCount !== row.projects.length ||
    billingCents(row.timeAmount) !==
      sum(row.projects.map(group => group.timeAmount)) ||
    billingCents(row.expenseAmount) !==
      sum(row.projects.map(group => group.expenseAmount)) ||
    billingCents(row.totalAmount) !==
      billingCents(row.timeAmount) + billingCents(row.expenseAmount) ||
    (entries.size + expenses.size > 0 && row.period === null)
  )
    fail()
  // Rounded hours/rates are display values: summing separately rounded groups
  // need not equal the aggregate of their original source durations.
  return {
    entityId: row.entityId,
    entityName: row.entityName,
    currency: row.currency,
    period: projectPeriod(row.period),
    totalAmount: row.totalAmount,
    timeAmount: row.timeAmount,
    expenseAmount: row.expenseAmount,
    totalHours: row.totalHours,
    entryCount: row.entryCount,
    expenseCount: row.expenseCount,
    projectCount: row.projectCount,
    memberCount: row.memberCount,
    projects: row.projects.map(group => ({
      projectId: group.projectId,
      projectName: group.projectName,
      projectCode: group.projectCode,
      hours: group.hours,
      totalAmount: group.totalAmount,
      timeAmount: group.timeAmount,
      expenseAmount: group.expenseAmount,
      entryCount: group.entryCount,
      expenseCount: group.expenseCount,
      lines: group.lines.map(projectTimeLine),
      expenseLines: group.expenseLines.map(projectExpenseLine),
      retainerContracts: group.retainerContracts.map(contract => ({
        contractId: contract.contractId,
        contractTitle: contract.contractTitle,
      })),
      exclusions: group.exclusions.map(projectTimeExclusion),
      expenseExclusions: group.expenseExclusions.map(projectExpenseExclusion),
    })),
    exclusions: row.exclusions.map(projectTimeExclusion),
    expenseExclusions: row.expenseExclusions.map(projectExpenseExclusion),
  }
}
export function billingQueue(
  value: unknown,
  params: { periodStart?: string; periodEnd?: string },
): BillingRunQueue {
  const parsed = queueSchema.safeParse(value)
  if (!parsed.success) return fail()
  const row = parsed.data
  assertPeriod(row.period, params, false)
  unique(row.clients.map(client => client.entityId))
  unique(row.byCurrency.map(total => total.currency))
  const receipts = new Set<string>(),
    totals = new Map<string, { cents: bigint; count: number }>()
  for (const client of row.clients) {
    unique(client.groups.map(group => group.groupId))
    assertExclusions([], client.exclusions)
    for (const group of client.groups) {
      assertExpenses(group.lines, group.groupId, params, receipts)
      if (
        group.expenseCount !== group.lines.length ||
        billingCents(group.amount) !== sum(group.lines.map(line => line.amount))
      )
        fail()
    }
    if (
      client.expenseCount !==
        client.groups.reduce((total, group) => total + group.expenseCount, 0) ||
      billingCents(client.totalAmount) !==
        sum(client.groups.map(group => group.amount))
    )
      fail()
    if (client.expenseCount) {
      const total = totals.get(client.currency) ?? { cents: 0n, count: 0 }
      total.cents += billingCents(client.totalAmount)
      total.count += client.expenseCount
      totals.set(client.currency, total)
    }
  }
  const expected = [...totals].sort(([a], [b]) => a.localeCompare(b))
  if (
    row.expenseCount !== receipts.size ||
    row.byCurrency.length !== expected.length ||
    row.byCurrency.some((total, index) => {
      const target = expected[index]
      return (
        !target ||
        total.currency !== target[0] ||
        total.expenseCount !== target[1].count ||
        billingCents(total.totalAmount) !== target[1].cents
      )
    })
  )
    fail()
  const single = row.byCurrency.length === 1 ? row.byCurrency[0] : undefined
  if (
    row.currency !== (single?.currency ?? null) ||
    row.totalAmount !== (single?.totalAmount ?? null)
  )
    fail()
  return {
    period: projectPeriod(row.period),
    totalAmount: row.totalAmount,
    currency: row.currency,
    expenseCount: row.expenseCount,
    byCurrency: row.byCurrency.map(total => ({
      currency: total.currency,
      totalAmount: total.totalAmount,
      expenseCount: total.expenseCount,
    })),
    clients: row.clients.map(client => ({
      entityId: client.entityId,
      entityName: client.entityName,
      currency: client.currency,
      expenseCount: client.expenseCount,
      totalAmount: client.totalAmount,
      groups: client.groups.map(group => ({
        groupId: group.groupId,
        groupName: group.groupName,
        expenseCount: group.expenseCount,
        amount: group.amount,
        lines: group.lines.map(projectExpenseLine),
      })),
      exclusions: client.exclusions.map(projectExpenseExclusion),
    })),
  }
}

export interface BillingScheduleReadFilters {
  entityId?: string
  projectId?: string
  contractId?: string
  billingAuthorizationId?: string
  categoryId?: string
  status?: string | string[]
  billingMonth?: string
  fromDate?: string
  toDate?: string
  search?: string
  page?: number
  limit?: number
}
const timestamp = z.union([
  z.date(),
  z
    .string()
    .datetime({ offset: true })
    .refine(value => isCalendarDate(value.slice(0, 10)))
    .transform(value => new Date(value)),
])
const categorySchema = BillingScheduleCategorySchema.extend({
  createdAt: timestamp,
  updatedAt: timestamp,
  deletedAt: timestamp.nullable().optional(),
})
const scheduleLineSchema = BillingScheduleLineSchema.extend({
  amount: z.number().finite(),
  quantity: z.number().finite().nullable().optional(),
  unitPrice: z.number().finite().nullable().optional(),
  currency,
  status: BillingScheduleLineStatusSchema,
  cadence: BillingScheduleCadenceSchema,
  sourceType: BillingScheduleLineSourceSchema,
  createdAt: timestamp,
  updatedAt: timestamp,
  deletedAt: timestamp.nullable().optional(),
  billingDate: timestamp.nullable().optional(),
  billingMonth: timestamp.nullable().optional(),
  amountEditedAt: timestamp.nullable().optional(),
  category: categorySchema.nullable().optional(),
})
const schedulePageSchema = z.object({
  data: z.array(scheduleLineSchema),
  meta: PaginationMetaSchema,
  message: z.string().optional(),
})
export function assertScheduleFilters(params: BillingScheduleReadFilters = {}) {
  for (const key of [
    'entityId',
    'projectId',
    'contractId',
    'billingAuthorizationId',
    'categoryId',
  ] as const)
    if (params[key] !== undefined && !isValidUUID(params[key])) fail()
  for (const key of ['fromDate', 'toDate'] as const)
    if (params[key] !== undefined && !isCalendarDate(params[key])) fail()
  if (params.fromDate && params.toDate && params.fromDate > params.toDate)
    fail()
  if (
    params.billingMonth &&
    (!/^\d{4}-\d{2}(?:-\d{2})?$/.test(params.billingMonth) ||
      !isCalendarDate(
        params.billingMonth.length === 7
          ? `${params.billingMonth}-01`
          : params.billingMonth,
      ))
  )
    fail()
  if (
    params.status !== undefined &&
    !(Array.isArray(params.status)
      ? params.status.every(
          status => BillingScheduleLineStatusSchema.safeParse(status).success,
        )
      : BillingScheduleLineStatusSchema.safeParse(params.status).success)
  )
    fail()
  if (
    params.page !== undefined &&
    (!Number.isSafeInteger(params.page) || params.page < 1)
  )
    fail()
  if (
    params.limit !== undefined &&
    (!Number.isSafeInteger(params.limit) || params.limit < 1)
  )
    fail()
  if (
    (params.page ?? 1) >
    Math.floor(Number.MAX_SAFE_INTEGER / Math.min(params.limit ?? 25, 500))
  )
    fail()
}
export function billingSchedulePage(
  value: unknown,
  params: BillingScheduleReadFilters,
  organizationId: string,
) {
  const parsed = schedulePageSchema.safeParse(value)
  if (!parsed.success) return fail()
  const row = parsed.data,
    meta = row.meta,
    page = params.page ?? 1,
    limit = Math.min(params.limit ?? 25, 500)
  if (
    ![
      meta.currentPage,
      meta.itemsPerPage,
      meta.totalItems,
      meta.totalPages,
    ].every(Number.isSafeInteger) ||
    meta.currentPage !== page ||
    meta.itemsPerPage !== limit ||
    meta.totalPages !== Math.ceil(meta.totalItems / limit) ||
    meta.hasNextPage !== page < meta.totalPages ||
    meta.hasPreviousPage !== page > 1 ||
    row.data.length !==
      Math.min(limit, Math.max(0, meta.totalItems - (page - 1) * limit))
  )
    fail()
  unique(row.data.map(line => line.id))
  for (const line of row.data) {
    if (
      line.organizationId !== organizationId ||
      line.deletedAt ||
      !isBillingNumericAmount(line.amount) ||
      !/^[A-Z]{3}$/.test(line.currency) ||
      (line.quantity != null && !isBillingNumericAmount(line.quantity, 8)) ||
      (line.unitPrice != null && !isBillingNumericAmount(line.unitPrice)) ||
      (
        [
          'entityId',
          'projectId',
          'contractId',
          'billingAuthorizationId',
          'categoryId',
        ] as const
      ).some(key => params[key] && line[key] !== params[key]) ||
      (line.category &&
        (line.category.id !== line.categoryId ||
          line.category.organizationId !== organizationId ||
          line.category.deletedAt)) ||
      (line.entity && line.entity.id !== line.entityId) ||
      (line.invoice && line.invoice.id !== line.invoiceId)
    )
      fail()
    if (
      params.status &&
      (Array.isArray(params.status)
        ? params.status.length > 0 && !params.status.includes(line.status)
        : line.status !== params.status)
    )
      fail()
    for (const value of [line.billingDate, line.billingMonth])
      if (
        value &&
        (value.getUTCHours() !== 0 ||
          value.getUTCMinutes() !== 0 ||
          value.getUTCSeconds() !== 0 ||
          value.getUTCMilliseconds() !== 0)
      )
        fail()
    const billingDay = line.billingDate
      ? calendarDateFromStorage(line.billingDate)
      : null
    const bucket = line.billingMonth ?? line.billingDate
    if (
      (params.fromDate && (!billingDay || billingDay < params.fromDate)) ||
      (params.toDate && (!billingDay || billingDay > params.toDate)) ||
      (params.billingMonth &&
        (!bucket ||
          calendarDateFromStorage(bucket).slice(0, 7) !==
            params.billingMonth.slice(0, 7)))
    )
      fail()
  }
  return row
}
